import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Edit, Trash2, X, Wand2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaScreen, CinemaScreenSection, CinemaSeatType } from '@/types';

const sectionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  rowCount: z.number().min(1, 'At least 1 row required'),
  seatsPerRow: z.number().min(1, 'At least 1 seat per row required'),
  rowLabels: z.string().optional(),
  seatNumberStart: z.number().min(1, 'Starting seat number must be at least 1'),
  positionX: z.number(),
  positionY: z.number(),
  rotation: z.number().min(-180).max(180),
  widthPx: z.number().min(0),
  heightPx: z.number().min(0),
  sortOrder: z.number(),
});

type SectionFormData = z.infer<typeof sectionSchema>;

export default function ScreenSections() {
  const { venueId, screenId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState<CinemaScreenSection | null>(null);
  const [generatingSeats, setGeneratingSeats] = useState<string | null>(null);

  const { data: screen } = useQuery<CinemaScreen>({
    queryKey: ['cinema-screen', screenId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}`);
      return response.data;
    },
  });

  const { data: sections, isLoading } = useQuery<CinemaScreenSection[]>({
    queryKey: ['cinema-sections', screenId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}/sections`);
      return response.data;
    },
  });

  const { data: seatTypes } = useQuery<CinemaSeatType[]>({
    queryKey: ['cinema-seat-types', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/seat-types`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SectionFormData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      name: '',
      description: '',
      rowCount: 5,
      seatsPerRow: 10,
      rowLabels: '',
      seatNumberStart: 1,
      positionX: 0,
      positionY: 0,
      rotation: 0,
      widthPx: 0,
      heightPx: 0,
      sortOrder: 0,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: SectionFormData) => {
      await api.post(`/admin/venues/${venueId}/cinema/screens/${screenId}/sections`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-sections', screenId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: SectionFormData) => {
      await api.put(
        `/admin/venues/${venueId}/cinema/screens/${screenId}/sections/${editingSection?.id}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-sections', screenId] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/screens/${screenId}/sections/${sectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-sections', screenId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
    },
  });

  const generateSeatsMutation = useMutation({
    mutationFn: async ({ sectionId, defaultSeatTypeId }: { sectionId: string; defaultSeatTypeId?: string }) => {
      const response = await api.post(
        `/admin/venues/${venueId}/cinema/screens/${screenId}/sections/${sectionId}/generate-seats`,
        { defaultSeatTypeId }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cinema-sections', screenId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
      setGeneratingSeats(null);
      alert(`Generated ${data.generated} seats`);
    },
  });

  const openModal = (section?: CinemaScreenSection) => {
    if (section) {
      setEditingSection(section);
      reset({
        name: section.name,
        description: section.description || '',
        rowCount: section.row_count,
        seatsPerRow: section.seats_per_row,
        rowLabels: section.row_labels || '',
        seatNumberStart: section.seat_number_start || 1,
        positionX: section.position_x,
        positionY: section.position_y,
        rotation: section.rotation || 0,
        widthPx: section.width_px || 0,
        heightPx: section.height_px || 0,
        sortOrder: section.sort_order,
      });
    } else {
      setEditingSection(null);
      reset({
        name: '',
        description: '',
        rowCount: 5,
        seatsPerRow: 10,
        rowLabels: '',
        seatNumberStart: 1,
        positionX: 0,
        positionY: 80 + (sections?.length || 0) * 150,
        rotation: 0,
        widthPx: 0,
        heightPx: 0,
        sortOrder: (sections?.length || 0) + 1,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSection(null);
    reset();
  };

  const onSubmit = (data: SectionFormData) => {
    if (editingSection) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema/screens`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Screen Sections</h1>
          <p className="text-sm text-gray-500">{screen?.name} - Configure seating sections</p>
        </div>
        <Link
          to={`/venues/${venueId}/cinema/screens/${screenId}/layout`}
          className="btn btn-secondary"
        >
          View Layout
        </Link>
        <button onClick={() => openModal()} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Section
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Section
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Seats/Row
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Seats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                  </tr>
                ))
              ) : sections?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No sections configured. Add sections like "Front Stalls", "Back Stalls", "Balcony".
                  </td>
                </tr>
              ) : (
                sections?.map((section) => (
                  <tr key={section.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{section.name}</p>
                        {section.description && (
                          <p className="text-sm text-gray-500">{section.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{section.row_count}</td>
                    <td className="px-6 py-4 text-sm">{section.seats_per_row}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {section.seat_count || section.row_count * section.seats_per_row} seats
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        section.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {section.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setGeneratingSeats(section.id)}
                          className="p-2 rounded-md hover:bg-gray-100 text-purple-500 hover:text-purple-700"
                          title="Generate Seats"
                        >
                          <Wand2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openModal(section)}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this section and all its seats?')) {
                              deleteMutation.mutate(section.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editingSection ? 'Edit Section' : 'Add Section'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="input"
                  placeholder="e.g., Front Stalls, Balcony"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  {...register('description')}
                  className="input"
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Rows *
                  </label>
                  <input
                    type="number"
                    {...register('rowCount', { valueAsNumber: true })}
                    className="input"
                    min={1}
                  />
                  {errors.rowCount && (
                    <p className="text-sm text-red-600 mt-1">{errors.rowCount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seats per Row *
                  </label>
                  <input
                    type="number"
                    {...register('seatsPerRow', { valueAsNumber: true })}
                    className="input"
                    min={1}
                  />
                  {errors.seatsPerRow && (
                    <p className="text-sm text-red-600 mt-1">{errors.seatsPerRow.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Row Labels (comma-separated)
                  </label>
                  <input
                    type="text"
                    {...register('rowLabels')}
                    className="input"
                    placeholder="A,B,C,D,E or leave empty"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty for A, B, C...
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starting Seat Number
                  </label>
                  <input
                    type="number"
                    {...register('seatNumberStart', { valueAsNumber: true })}
                    className="input"
                    min={1}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    First seat number per row
                  </p>
                  {errors.seatNumberStart && (
                    <p className="text-sm text-red-600 mt-1">{errors.seatNumberStart.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position X
                  </label>
                  <input
                    type="number"
                    {...register('positionX', { valueAsNumber: true })}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position Y
                  </label>
                  <input
                    type="number"
                    {...register('positionY', { valueAsNumber: true })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rotation (degrees)
                </label>
                <input
                  type="number"
                  {...register('rotation', { valueAsNumber: true })}
                  className="input"
                  min={-180}
                  max={180}
                  step={5}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Rotate section for events with central stage (-180 to 180)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    {...register('widthPx', { valueAsNumber: true })}
                    className="input"
                    min={0}
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = auto</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    {...register('heightPx', { valueAsNumber: true })}
                    className="input"
                    min={0}
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = auto</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingSection
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Seats Modal */}
      {generatingSeats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Generate Seats</h2>
              <button onClick={() => setGeneratingSeats(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              This will delete all existing seats in this section and generate new ones based on the
              row count and seats per row configuration.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Seat Type (optional)
              </label>
              <select
                id="defaultSeatType"
                className="input"
              >
                <option value="">No type (assign later)</option>
                {seatTypes?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setGeneratingSeats(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const select = document.getElementById('defaultSeatType') as HTMLSelectElement;
                  generateSeatsMutation.mutate({
                    sectionId: generatingSeats,
                    defaultSeatTypeId: select.value || undefined,
                  });
                }}
                disabled={generateSeatsMutation.isPending}
                className="btn btn-primary"
              >
                {generateSeatsMutation.isPending ? 'Generating...' : 'Generate Seats'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
